<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeUnmount,
  onMounted,
  nextTick,
  inject,
  provide,
} from "vue";
import SlideMenu from "@/v2/components/SlideMenu/SlideMenu.vue";
import Dialog from "@/v2/components/Dialog/Dialog.vue";
import QRCode from "@/v2/components/HomeWidgets/QRCode.vue";
import { SLIDE_MENU_OVERLAY_WIDTH_TRANSITION_MS } from "@/v2/shared/constants.js";
import * as appIdle from "@/components/Global/providedData/appIdle.ts";
import * as autoOpenSearch from "@/components/Global/providedData/autoOpenSearch.ts";
import useSkipToContent from "@/layouts/useSkipToContent.ts";

const { mainContent } = useSkipToContent();

const { isIdle } = inject(appIdle.key, appIdle.defaultApi);

const { shouldAutoOpenSearch, disableAutoOpenSearch } = inject(
  autoOpenSearch.key,
  autoOpenSearch.defaultApi
);

const props = defineProps({
  isFullBleedContent: {
    type: Boolean,
    default: false,
  },
  sideMenuOpenOnInit: {
    type: Boolean,
    default: false,
  },
  isSponsorBarHidden: {
    type: Boolean,
    default: false,
  },
  focusTheme: {
    type: String,
    default: "light",
  },
});

const emit = defineEmits(["onPageUnmounted", "onPageIdle"]);

const slideMenuInline = ref(null);
const disableSlideMenuInlineTransition = ref(props.sideMenuOpenOnInit);

const slideMenuOverlay = ref(null);
const qrDialog = ref(null);

const remountSlideMenuOverlayTimeout = ref(null);
const isSlideMenuOverlaySlotMounted = ref(true);

function triggerResize() {
  window.dispatchEvent(new Event("resize"));
}

function openSlideMenuInline() {
  slideMenuInline.value.open();
}

function closeSlideMenuInline() {
  slideMenuInline.value.close();
}

function openQrDialog() {
  qrDialog.value.isOpen = true;
}

const isSlideMenuInlineOpen = computed(() => slideMenuInline.value?.isOpen);

watch(
  () => slideMenuInline.value?.isOpen,
  () => {
    triggerResize();
  }
);

function remountSlideMenuOverlaySlot() {
  if (remountSlideMenuOverlayTimeout.value) {
    clearTimeout(remountSlideMenuOverlayTimeout.value);
  }

  remountSlideMenuOverlayTimeout.value = setTimeout(() => {
    isSlideMenuOverlaySlotMounted.value = false;

    nextTick(() => {
      isSlideMenuOverlaySlotMounted.value = true;
    });
  }, SLIDE_MENU_OVERLAY_WIDTH_TRANSITION_MS);
}

onBeforeUnmount(() => {
  if (remountSlideMenuOverlayTimeout.value) {
    clearTimeout(remountSlideMenuOverlayTimeout.value);
  }
  emit("onPageUnmounted", {
    siteNavigatorIsOpen: slideMenuInline.value?.isOpen,
  });
});

watch(
  () => slideMenuOverlay.value?.isOpen,
  () => {
    remountSlideMenuOverlaySlot();
  }
);

function openSlideMenuOverlay() {
  slideMenuOverlay.value.open();
}

function closeSlideMenuOverlay() {
  slideMenuOverlay.value.close();
}

watch(
  () => isIdle.value,
  () => {
    if (isIdle.value) {
      closeSlideMenuInline();
      closeSlideMenuOverlay();
      emit("onPageIdle");
    }
  }
);

function resetSlideMenuInlineTransition() {
  if (!disableSlideMenuInlineTransition.value) {
    return;
  }

  setTimeout(() => {
    disableSlideMenuInlineTransition.value = false;
  }, 1000);
}

onMounted(resetSlideMenuInlineTransition);

onMounted(() => {
  if (shouldAutoOpenSearch.value) {
    openSlideMenuOverlay();
  }
});

function onSlideMenuOverlayVisiblityChanged(isOpen) {
  if (!isOpen) {
    disableAutoOpenSearch();
  }
}

// Injected into child layouts, eg. Carousel,
// CategoryFilterLayout, CategoryHorizontalLayout,
// where layout needs the inline slide menu state & actions
provide(
  "slideMenuInline",
  ref({
    isOpen: isSlideMenuInlineOpen,
    openSlideMenuInline,
    closeSlideMenuInline,
  })
);
</script>

<template>
  <div class="page-with-buttons" :focus-theme>
    <slot
      name="button-zones"
      :open-slide-menu-inline
      :open-slide-menu-overlay
      :open-qr-dialog
    />
    <div
      class="page-with-buttons__slide-menu-inline-container"
      :class="[
        {
          'page-with-buttons__slide-menu-inline-container--is-open':
            isSlideMenuInlineOpen,
          'page-with-buttons__slide-menu-inline-container--disable-transition':
            disableSlideMenuInlineTransition,
        },
      ]"
    >
      <SlideMenu
        ref="slideMenuInline"
        :use-surface-colors="false"
        :open-on-init="sideMenuOpenOnInit"
      >
        <slot name="slide-menu-inline-content" />
      </SlideMenu>
    </div>
    <div
      ref="mainContent"
      class="page-with-buttons__content-container"
      tabindex="-1"
      aria-label="Main content"
      role="group"
      :class="{
        'page-with-buttons__content-container--is-full-bleed':
          isFullBleedContent,
      }"
    >
      <slot />
    </div>
    <div
      class="page-with-buttons__sponsor-container"
      :class="{
        'page-with-buttons__sponsor-container--is-open': isSlideMenuInlineOpen,
        'page-with-buttons__sponsor-container--is-hidden': isSponsorBarHidden,
      }"
    >
      <slot name="sponsor-bar" />
    </div>
    <div class="page-with-buttons__overlay-menu">
      <SlideMenu
        ref="slideMenuOverlay"
        is-overlay
        should-teleport
        :is-side-menu="false"
        :has-fade="false"
        @open="onSlideMenuOverlayVisiblityChanged"
      >
        <slot
          v-if="isSlideMenuOverlaySlotMounted"
          name="slide-menu-overlay-content"
          :close-slide-menu-overlay
        />
      </SlideMenu>
    </div>
    <Dialog ref="qrDialog">
      <QRCode />
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "@/v2/styles/main";
@import "@/v2/components/SlideMenu/slide-menu";

.page-with-buttons {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 1; // ensures that sponsor bar is at the bottom
  padding-left: grid-column(0, 1);
  padding-right: grid-column(0, 1);
  overflow: hidden;

  @include min-width-sm {
    padding-left: 0;
    padding-right: grid-column(0, 5);
    flex-grow: 0;
  }

  @include min-width-sm-and-height-sm {
    height: 100%;
  }

  @include massive-screen {
    height: 60dvh;
    margin-top: auto;
  }

  &__slide-menu-inline-container {
    position: relative;
    min-width: grid-column(0, 1);
    height: 100%;
    pointer-events: none;
    transition: all $slide-menu-transition-time $slide-menu-easing;
    z-index: var(--z-index-site-nav-menu);
    display: none;
    border-radius: 0 var(--border-radius-large) 0 0;
    overflow: hidden;

    @include min-width-sm {
      min-width: grid-column(0, 5);
      display: block;

      &--is-open {
        min-width: grid-column(3, 8);
        margin-right: var(--spacing-base);
      }
    }

    &--disable-transition {
      transition: none;
    }
  }

  &__content-container {
    max-width: 100%;
    display: flex;
    flex-grow: 1;
    height: 100%;
    z-index: 0;
    min-inline-size: 0;

    @include min-width-sm {
      padding-top: var(--spacing-base);
    }

    &--is-full-bleed {
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      padding-top: 0;
    }

    &:focus-visible {
      outline-offset: -5px;
      border-radius: var(--border-radius-medium);
    }
  }

  &__sponsor-container {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: var(--z-index-sponsor-container);
    transition: all $slide-menu-transition-time $slide-menu-easing;
    pointer-events: none;

    &--is-open {
      width: 100%;

      @include min-width-sm {
        width: grid-column(9);
        padding-left: grid-column(3, 7);
      }
    }

    &--is-hidden {
      pointer-events: none;
    }
  }

  &__overlay-menu {
    z-index: var(--z-index-search-overlay);
    display: none;

    @include min-width-sm {
      display: block;
    }
  }
}
</style>
